import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Splash from "../components/common/Splash";
import Footer from "../layout/Footer";
import Header from "../layout/Header";
import useSEO from "../hooks/useSEO";
import SeoPanel from "../components/seo/SeoPanel";
import { buildBreadcrumbJsonLd, getSiteBrand } from '../utils/seo';
import { providerLogoBackgroundImage } from '../utils/providerLogo';
import Toolbar from "../navigation/Toobar";
import { posterFromSlug } from '../utils/posterFromSlug';

function Games({globalData}){
    const [games, setGames] = useState([]);
    const [filteredGames, setFilteredGames] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [isMobile, setIsMobile] = useState(false);
    const [providers, setProviders] = useState([]);
    const [setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoggedIn] = useState(!!localStorage.getItem('token')); 
    
    const navigate = useNavigate();
    const location = useLocation();
    const origin = window.location.origin;

    const [searchParams] = useSearchParams();
    const type = searchParams.get('type');
    const system = searchParams.get('system');
    const title = searchParams.get('title');

    const [activeProvider, setActiveProvider] = useState(system ? system.split(',') : []);

    const brandName = globalData?.general?.site_title || getSiteBrand();
    const categoryLabel = title || (type ? type.charAt(0) + type.slice(1).toLowerCase() : '');

    useSEO({
      title: categoryLabel ? `${categoryLabel} Games – Play Online` : 'Games, Slots & Sports Lobby',
      description: categoryLabel
        ? `Play ${categoryLabel} games online at ${brandName}. Browse every provider in the ${categoryLabel} lobby, filter by studio and start playing for real money in BDT.`
        : `Browse the full games lobby at ${brandName}: live casino, slots, table games, crash, fishing and sports. Filter by provider and play in BDT on mobile or desktop.`,
      keywords: categoryLabel
        ? `${categoryLabel} games, play ${categoryLabel} online, ${categoryLabel} provider list, real money gaming bangladesh`
        : 'online casino games, slot games bangladesh, live casino, game providers, crash games, fishing games',
      path: '/games',
      brand: brandName,
      jsonLd: [
        buildBreadcrumbJsonLd([
          { name: 'Home', path: '/' },
          { name: categoryLabel ? `${categoryLabel} Games` : 'Games', path: '/games' },
        ]),
      ],
    });

    const filtterOpen = () => {
        const chatElement = document.querySelector('.searchpage');
        chatElement.classList.add('active');
        const subEmlemt = document.querySelector('.searchpage-bar');
        subEmlemt.classList.add('active');
    };

    const filtterClose = () => {
        const chatElement = document.querySelector('.searchpage');
        chatElement.classList.remove('active');
        const subEmlemt = document.querySelector('.searchpage-bar');
        subEmlemt.classList.remove('active');
    };

    // Memoize fetchGames and fetchSystems
    const fetchGames = useCallback((currentSystem) => {
        const url = `${process.env.REACT_APP_API_URL}/api/get-games?type=${type}&system=${currentSystem}`;
        console.log('Fetching data from:', url);
        fetch(url)
          .then(response => response.json())
          .then(data => {
            setGames(data.games);
            setFilteredGames(data.games);
            console.log(data);
          })
          .catch(error => {
            console.error('Error fetching games:', error);
            setError('Failed to fetch games');
          });
    }, [type, setError]);

    // Move fetchProviders function above useCallback
    const fetchProviders = useCallback(async (systems) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/get-provider?system=${JSON.stringify(systems)}`);
            const data = await response.json();
            if (response.ok) {
                setProviders(data.data || []);
            } else {
                setError(data.message || 'Failed to fetch providers');
            }
        } catch (error) {
            console.error('Error fetching providers:', error);
            setError('An unexpected error occurred while fetching providers');
        }
    }, [setError]);

    const fetchSystems = useCallback(async (type) => {
        setIsLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/get-system?type=${type}`);
            const data = await response.json();
            if (response.ok) {
                fetchProviders(data.systems); // Fetch providers once systems are fetched
            } else {
                setError(data.message || 'Failed to fetch systems');
            }
        } catch (error) {
            console.error('Error fetching systems:', error);
            setError('An unexpected error occurred while fetching systems');
        } finally {
            setIsLoading(false);
        }
    }, [fetchProviders, setError]);

    const handleSearch = (event) => {
        const query = event.target.value.toLowerCase();
        setSearchQuery(query);
        const filtered = games.filter(game => game.name.toLowerCase().includes(query));
        setFilteredGames(filtered);
    };

    const handleGameClick = async (gameCode, gamePlatform, gameType) => {
      if (!isLoggedIn) {
        navigate('/login');
        return;
      }
      setIsLoading(true);

      try {
        const token = localStorage.getItem('token');
        const remote_id = localStorage.getItem('remote_id');
        if (!token || !remote_id) {
          setError('User not logged in');
          return;
        }

        let resolvedPlatform = gamePlatform;
        let resolvedType = gameType;
        if ((!resolvedPlatform || !resolvedType) && typeof gameCode === 'string' && gameCode.includes('-')) {
          const parts = gameCode.split('-');
          if (parts.length >= 2) {
            resolvedPlatform = parts[0];
            resolvedType = parts[1];
          }
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/inr-game-run`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId: remote_id,
            externalURL: origin,
            isMobileLogin: isMobile,
            gameCode: gameCode,
            gameType: resolvedType,
            platform: resolvedPlatform,
          }),
        });

        const data = await response.json();
        if (response.ok) {
          const iframeUrl = data?.url || data?.data?.payload?.game_launch_url;
          if (iframeUrl) {
            navigate('/gamerun', {
              state: {
                iframeUrl,
                fromParams: { type, system, title },
              },
            });
          } else {
            setError(data.message || 'Missing game URL in response');
          }
        } else {
          setError(data.message || 'An unexpected error occurred');
        }
      } catch (error) {
        setError('An unexpected error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    const handleProviderClick = (providerSystem) => {
      let updatedProviders = [];
    
      if (providerSystem === "ALL") {
        const shouldSelectAll = activeProvider.length !== providers.length;
        updatedProviders = shouldSelectAll ? providers.map(provider => provider.platform) : [];
      } else {
        updatedProviders = activeProvider.includes(providerSystem)
          ? activeProvider.filter(provider => provider !== providerSystem)
          : [...activeProvider, providerSystem];
      }
    
      setActiveProvider(updatedProviders);
      fetchGames(updatedProviders);
    };

    const [imageLoaded, setImageLoaded] = useState({});

    const handleImageError = (gameCode) => {
        setImageLoaded((prev) => ({
            ...prev,
            [gameCode]: false,
        }));
    };

    const handleImageLoad = (gameCode) => {
        setImageLoaded((prev) => ({
            ...prev,
            [gameCode]: true,
        }));
    };
    useEffect(() => {
  if (system === "ALL" && providers.length > 0) {
    const allPlatforms = providers.map(p => p.platform);
    setActiveProvider(allPlatforms);
    fetchGames(allPlatforms);
  }
}, [system, providers, fetchGames]);


    useEffect(() => {
      if (type && system) {
          fetchGames(system);
          fetchSystems(type);
      } else {
          console.error("Type or system is missing", { type, system });
      }

      const isMobileDevice = window.innerWidth <= 768;
      setIsMobile(isMobileDevice);
    }, [type, system, fetchGames, fetchSystems]);
    
    if(isLoading) {
      return(
     <Splash/>
      );
    }
    return(
<div className="v-application--wrap">
  <div>
    {/**/}
    <div className="row no-gutters">
     <Header/>
    </div>
 
   
    <div className="body">
      <div className="px-2">
        <div className="row no-gutters">
          <div className="col col-12 mobile-padding">
            <div className="slot-game-page-card-menu hidden-md-and-up text-center v-card v-sheet theme--dark">
              <button
                type="button"
                className="v-icon notranslate mobile-header-navArrow v-icon--link mdi  theme--dark"
                style={{ fontSize: "36px" }}
              />
              {title}
              <button
                type="button"
                className="v-icon notranslate mobile-header-navArrow v-icon--link mdi  theme--dark"
                style={{ fontSize: "36px" }}
              />
            </div>
            <div
              className="hidden-md-and-up  v-card v-sheet theme--dark"
              style={{ borderRadius: "unset" }}
            >
              <div className="mobile-provider-list">
                <div  onClick={() => handleProviderClick("ALL")} className={`mobile-provider-card all-provider-div  ${activeProvider.length === providers.length ? 'selected' : ''} `}>
                  <span className="all-provider text-center">All</span>
                </div>
                  {providers.map((provider) => (
                <div   onClick={() => handleProviderClick(provider.platform)} className={`mobile-provider-card ${activeProvider.includes(provider.platform)? 'selected' : ''}`}>
                  <div
                    className="v-image v-responsive provider-card-img theme--dark"
                    style={{ height: "60px", maxHeight: "60px", width: "auto" }}
                  >
                    <div
                      className="v-responsive__sizer"
                      style={{ paddingBottom: "100%" }}
                    />
                      <div
                      className="v-image__image v-image__image--cover"
                      style={{
                        backgroundImage:
                          providerLogoBackgroundImage(provider.platform),
                        backgroundPosition: "center center",
                      }}
                    />
                    <div
                      className="v-responsive__content"
                      style={{ width: "300px" }}
                    />
                  </div>
                  <span className="text-center mobile-vendor-name">{provider.platform}</span>
                </div>
                 ))}
             
             
          
               
               
              </div>
            </div>
            <div className="mx-auto gamesCardContainer v-card v-sheet theme--dark mb-4">
              
              <div className>
              
              
                <div className="row hidden-md-and-up no-gutters justify-end col-12 py-0">
                  <div className="col-md-6 col-lg-3 col-12 py-0 pr-3">
                    <div className="v-input search_game_text pl-0 theme--dark v-text-field v-text-field--single-line v-text-field--solo v-text-field--filled v-text-field--is-booted v-text-field--enclosed v-text-field--rounded v-select v-autocomplete">
                      <div className="v-input__control">
                        <div
                          role="combobox"
                          aria-haspopup="listbox"
                          aria-expanded="false"
                          aria-owns="list-2435"
                          className="v-input__slot"
                          style={{
                            backgroundColor: "rgb(255, 255, 255)",
                            borderColor: "rgb(255, 255, 255)",
                          }}
                        >
                          <div className="v-input__prepend-inner">
                            <i
                              aria-hidden="true"
                              className="v-icon notranslate mdi mdi-magnify theme--dark"
                              style={{
                                color: "rgb(149, 149, 149)",
                                caretColor: "rgb(149, 149, 149)",
                              }}
                            />
                          </div>
                          <div className="v-select__slot">
                            <label
                              htmlFor="input-2435"
                              className="v-label theme--dark"
                              style={{
                                left: "0px",
                                right: "auto",
                                position: "absolute",
                              }}
                            >
                              Search games
                            </label>
                             <input
                                type="text"
                                className="ng-untouched ng-pristine ng-valid"
                                placeholder="Search Games"
                                name="search"
                                value={searchQuery}
                                onChange={handleSearch}
                                autocomplete="off"
                                style={{ pointerEvents: 'all', color: '#000' }}
                            />
                            <div className="v-input__append-inner">
                              <div />
                            </div>
                            <div className="v-input__append-inner">
                              <div className="v-input__icon v-input__icon--append">
                                <button
                                  aria-hidden="true"
                                  type="button"
                                  tabIndex={-1}
                                  className="v-icon notranslate v-icon--link mdi mdi-menu-down theme--dark"
                                />
                              </div>
                            </div>
                           
                          </div>
                          <div className="v-menu" />
                        </div>
                        <div className="v-text-field__details">
                          <div className="v-messages theme--dark">
                            <div className="v-messages__wrapper" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  role="list"
                  className="v-list slot-game-card-select px-2 v-sheet theme--dark"
                >
                
                 
            
               
              
               {filteredGames.length > 0 ? (
                        filteredGames.some((game) => imageLoaded[game.code] !== false) ? (
                          filteredGames.map(
                            (game) =>
                              imageLoaded[game.code] !== false && (
                
                  <div
                    tabIndex={-1}
                    role="listitem"
                     onClick={() => handleGameClick(game.uid, game.platform, game.type)}
                    className="v-list-item theme--dark px-0 slot-game-card-item text-center mb-2"
                  >
                    <div className="elevation-0 v-card v-sheet theme--dark">
                      <span
                        right
                        className="v-badge v-badge--overlap theme--dark"
                      >
                        <div
                          className="v-avatar game_icon_sizing gameButton"
                          style={{
                            height: "48px",
                            minWidth: "48px",
                            width: "48px",
                          }}
                        >
                          <div className="v-image v-responsive theme--dark">
                            <div
                              className="v-responsive__sizer"
                              style={{ paddingBottom: "100%" }}
                            />
                            <div
                              className="v-image__image v-image__image--cover"
                              style={{
                                backgroundImage: `url(${posterFromSlug(game)})`,
                                backgroundPosition: "center center",
                              }}
                            >
                                <img
                                src={posterFromSlug(game)}
                                alt={game.code}
                                style={{ display: "none" }}
                                onLoad={() => handleImageLoad(game.code)}
                                onError={() => handleImageError(game.code)}
                            />
                            </div>
                            <div
                              className="v-responsive__content"
                              style={{ width: "450px" }}
                            />
                          </div>
                         
                        </div>
                        
                      </span>
                    </div>
                    <span className="v-tooltip v-tooltip--bottom">{/**/}</span>
                    <div
                      aria-haspopup="true"
                      aria-expanded="false"
                      className="v-card__text pt-2 font-weight-bold slot-game-name-box text-center elevation-0"
                    >
                     {game.name}
                    </div>
                  </div>
                  )
                          )
                        ) : (
                         <></>
                        )
                      ) : (
                        <></>
                      )}
                
             
              
               
                 
                 
                
                
                
                
             
                
                </div>
                {/**/}
                
              </div>
            </div>
          </div>
        </div>
      </div>
        <SeoPanel
          id="seo-games"
          preview={
            <>
              <nav className="seo-breadcrumb" aria-label="Breadcrumb">
                <Link to="/">Home</Link> <span aria-hidden="true">›</span>{' '}
                <span aria-current="page">
                  {categoryLabel ? `${categoryLabel} Games` : 'Games'}
                </span>
              </nav>

              <h1 className="seo-h1" id="seo-games-title">
                {categoryLabel
                  ? `Play ${categoryLabel} games online at ${brandName}`
                  : `Online games and providers at ${brandName}`}
              </h1>
              <p className="seo-text">
                {categoryLabel
                  ? `This lobby lists every ${categoryLabel.toLowerCase()} provider available on ${brandName}. Tap a provider to filter the list, use the search box to find a title by name, then open the game to start playing for real money from your BDT wallet.`
                  : `Browse the complete games lobby at ${brandName}. Filter by provider or search by name to find live casino tables, slots, table games, crash, fishing and sports markets, all playable from the same BDT wallet.`}
              </p>
            </>
          }
        >
          <p className="seo-text">
            Your balance, bonuses and betting history stay in one account, so you can move from a
            cricket market to a slot or a live dealer table without transferring funds. Games load
            in the browser on mobile and desktop, and the Android app is available for faster
            loading on slower connections.
          </p>

          <ul className="seo-links">
            <li>
              <Link to="/promotion">Promotions and bonus offers</Link>
            </li>
            <li>
              <Link to="/faq">Deposit, withdrawal and turnover help</Link>
            </li>
            <li>
              <Link to="/about-us">About {brandName}</Link>
            </li>
            <li>
              <Link to="/contact-us">Contact support</Link>
            </li>
          </ul>
        </SeoPanel>

     <Footer sociallink={globalData.socialLinks || {}}/>
    </div>
   <Toolbar/>
    
  </div>
</div>


    );

}
export default Games;